import React, { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const CHECK_TIMEOUT_MS = 10000;

// Vite fingerprints the application entry whenever its compiled inputs change.
// Reading the public HTML checks the release without another API/version owner.
function applicationEntry(documentNode) {
    const entries = documentNode.querySelectorAll('script[type="module"][src]');
    for (const script of entries) {
        const url = new URL(script.getAttribute('src'), window.location.href);
        if (url.origin === window.location.origin && /^\/assets\/index-[^/]+\.js$/.test(url.pathname)) {
            return url.href;
        }
    }
    return null;
}

const reloadApplication = () => window.location.reload();

export default function ApplicationUpdateNotice({ onReload = reloadApplication }) {
    const [available, setAvailable] = useState(false);

    useEffect(() => {
        const loadedEntry = applicationEntry(document);
        // Local Vite sessions use HMR, rather than a released /assets entry.
        if (!loadedEntry) return undefined;
        let disposed = false;
        let found = false;
        let pending = null;

        const check = async () => {
            if (disposed || found || pending || document.visibilityState === 'hidden') return;
            const controller = new AbortController();
            pending = controller;
            const timeout = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
            try {
                const response = await fetch('/index.html', {
                    cache: 'no-store', credentials: 'omit', mode: 'same-origin', signal: controller.signal,
                });
                if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) return;
                const html = new DOMParser().parseFromString(await response.text(), 'text/html');
                const latestEntry = applicationEntry(html);
                if (!disposed && latestEntry && latestEntry !== loadedEntry) {
                    found = true;
                    setAvailable(true);
                }
            } catch {
                // Offline/failed checks must not interrupt an active editing session.
            } finally {
                window.clearTimeout(timeout);
                if (pending === controller) pending = null;
            }
        };

        check();
        const interval = window.setInterval(check, CHECK_INTERVAL_MS);
        window.addEventListener('focus', check);
        document.addEventListener('visibilitychange', check);
        return () => {
            disposed = true;
            window.clearInterval(interval);
            window.removeEventListener('focus', check);
            document.removeEventListener('visibilitychange', check);
            pending?.abort();
        };
    }, []);

    return <Snackbar open={available} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" variant="filled" action={<Button color="inherit" onClick={onReload}>Reload</Button>}>
            A new version is available. Save any unfinished changes, then reload.
        </Alert>
    </Snackbar>;
}
